import type { IColumnSortStateSlice } from './getEffective';
import type { IQuickDragItem } from '@/pages/datalab/components/QuickDragList/types';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import type { IColumnPercentageItem } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/columnPercentage';
import type { IDimensionPercentageConfig } from '../../../types/settings/dimensionPercentage';
import { syncColumnSortVirtualColumns } from '../../../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/syncColumnSortVirtualColumns';


export function selectFieldColumnSortingSettings(state: {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
}): IColumnSortStateSlice {
  return {
    base: {
      column_sort: state.base.column_sort,
      column_percentage: state.base.column_percentage,
      dimension_percentage: state.base.dimension_percentage,
    },
    draft: {
      column_sort: state.draft.column_sort,
      column_percentage: state.draft.column_percentage,
      dimension_percentage: state.draft.dimension_percentage,
    },
  };
}



/**
 * @description 场景 2（面板侧，拖拽即时写 draft）：QuickDragList → `column_sort` 载荷
 *
 * FieldColumnSorting 使用 QuickDragList 展示可拖拽列项；
 * 每项的 `source` 在构建列表时已挂好完整的 `IColumnSortItem`。
 * 本函数在 `onChange` 时把拖拽结果转为可 `UPDATE_DRAFT` 的数组。
 *
 * **为何返回 `undefined` 而非 `[]`**：
 * 空数组与「未设置 draft」在部分 reducer 语义上不同；返回 undefined 表示不写 draft，
 * 避免用空列表覆盖 base 里已有的 column_sort。
 */

/**
 * 将 QuickDragList 的 items 转为 `column_sort` 数组。
 *
 * - 从 `item.source` 取出 `IColumnSortItem`（由 buildFromQB / 列表构建阶段注入）
 * - 过滤缺少有效 `column` 的脏数据，防止写入 draft 后 Confirm 同步失败
 *
 * @param items - 拖拽组件当前列表（顺序即用户期望的列顺序）
 * @returns 至少一项有效时返回数组；否则 `undefined`（调用方不应 dispatch 空 column_sort）
 */
export function mapQuickDragItemsToColumnSort(
  items: IQuickDragItem[],
  options?: {
    columnPercentage?: IColumnPercentageItem[];
    dimensionPercentage?: IDimensionPercentageConfig;
  },
): IColumnSortItem[] | undefined {
  const list = items
    .map(item => item.source as IColumnSortItem | undefined)
    .filter(
      (item): item is IColumnSortItem =>
        !!item && typeof item.column === 'string' && item.column.length > 0,
    );
  if (!list.length) {
    return undefined;
  }
  return syncColumnSortVirtualColumns(
    list,
    options?.columnPercentage,
    options?.dimensionPercentage,
  );
}